import React from "react";
import { FaEye, FaPen, FaTrash } from "react-icons/fa";
import { MdDevices, MdLogout } from "react-icons/md";
import ProfilePhotoCell from "../common/ProfilePhotoCell";
import { TableSkeleton } from "../common/skeletons";
import { TH_CLASS, TD_BASE } from "./constants";

export default function UserProfilesTable({
  users,
  page,
  limit,
  isLoading,
  activeFiltersCount,
  onViewProfile,
  onEditUser,
  onDeleteUser,
  onOpenLoginDevices,
  onLogoutAllDevices,
  logoutBusyUserId,
  deletingUserId,
  showRowSelection,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  bulkDeleting,
}) {
  /** Prefer explicit prop; if omitted, match row delete capability so bulk UI cannot disappear alone. */
  const allowRowSelection = showRowSelection ?? Boolean(onDeleteUser);

  if (isLoading) {
    return <TableSkeleton rows={8} cols={16} />;
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-semibold text-gray-900">No users found</h3>
        <p className="mt-1 text-sm text-gray-500">
          {activeFiltersCount > 0
            ? "Try adjusting your filters"
            : "No user profiles match your criteria"}
        </p>
      </div>
    );
  }

  const pageIds = (users || []).map((u) => u.id).filter(Boolean);
  const allOnPageSelected =
    allowRowSelection && pageIds.length > 0 && pageIds.every((id) => (selectedIds || []).includes(id));

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 bg-white min-w-[960px] w-full">
        <thead className="bg-gray-50">
          <tr>
            {allowRowSelection && (
              <th className={`${TH_CLASS} w-12`}>
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  checked={allOnPageSelected}
                  disabled={bulkDeleting}
                  onChange={() => onToggleSelectAll && onToggleSelectAll()}
                  aria-label="Select all users on this page"
                />
              </th>
            )}
            <th className={`${TH_CLASS}`}>SR. NO</th>
            <th className={TH_CLASS}>Photo</th>
            <th className={TH_CLASS}>First Name</th>
            <th className={TH_CLASS}>Last Name</th>
            <th className={TH_CLASS}>Email</th>
            <th className={TH_CLASS}>Gender</th>
            <th className={TH_CLASS}>Contact No</th>
            <th className={TH_CLASS}>Age</th>
            <th className={TH_CLASS}>Profile %</th>
            <th className={TH_CLASS}>Referrals</th>
            <th className={TH_CLASS}>Height</th>
            <th className={TH_CLASS}>GrewUp</th>
            <th className={TH_CLASS}>Occupation</th>
            <th className={TH_CLASS}>Source</th>
            <th className={TH_CLASS}>Created</th>
            <th className={TH_CLASS}>Premium</th>
            <th className={`${TH_CLASS} sticky right-0 bg-gray-50`}>Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user, idx) => (
            <tr key={user.id} className="hover:bg-gray-50 transition-colors">
              {allowRowSelection && (
                <td className={`${TD_BASE} w-12`}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    checked={(selectedIds || []).includes(user.id)}
                    disabled={bulkDeleting || deletingUserId === user.id}
                    onChange={() => onToggleSelect && onToggleSelect(user.id)}
                    aria-label={`Select user ${user.id}`}
                  />
                </td>
              )}
              <td className={`${TD_BASE} font-medium text-gray-900`}>
                {(page - 1) * limit + idx + 1}
              </td>
              <td className={`${TD_BASE} w-[72px]`}>
                <ProfilePhotoCell
                  imageUrl={user.imageUrl}
                  displayName={`${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email}
                />
              </td>
              <td className={`${TD_BASE} font-medium text-gray-900`}>{user.firstName}</td>
              <td className={`${TD_BASE} text-gray-800`}>{user.lastName}</td>
              <td className={`px-6 py-4 text-xs md:text-sm text-gray-700 max-w-xs truncate`}>
                {user.email}
              </td>
              <td className={`${TD_BASE} text-gray-700 capitalize`}>{user.gender}</td>
              <td className={`${TD_BASE} text-gray-700`}>{user.contactNo}</td>
              <td className={`${TD_BASE} text-gray-700`}>{user.age}</td>
              <td className={`${TD_BASE} text-gray-700 tabular-nums font-medium`}>
                {user.percentage != null && user.percentage !== ""
                  ? `${Math.round(Number(user.percentage))}%`
                  : "—"}
              </td>
              <td className={`${TD_BASE} text-gray-700 tabular-nums font-medium`}>
                {user.referralCount != null ? Number(user.referralCount) : "—"}
              </td>
              <td className={`${TD_BASE} text-gray-700`}>{user.height}</td>
              <td className={`${TD_BASE} text-gray-700`}>{user.grewUp}</td>
              <td className={`${TD_BASE} text-gray-700`}>{user.occupation}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                    user.is_web
                      ? "bg-blue-100 text-blue-800 border-blue-200"
                      : "bg-green-100 text-green-800 border-green-200"
                  }`}>
                  {user.is_web ? "Web" : "Mobile"}
                </span>
              </td>
              <td className={`${TD_BASE} text-gray-600`}>
                {user.createdDate
                  ? new Date(user.createdDate).toLocaleDateString()
                  : "-"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                    user.premium === 1
                      ? "bg-amber-100 text-amber-800 border-amber-200"
                      : "bg-gray-100 text-gray-800 border-gray-200"
                  }`}>
                  {user.premium === 1 ? "Yes" : "No"}
                </span>
              </td>
              <td className={`${TD_BASE} sticky right-0 bg-white hover:bg-gray-50`}>
                <div className="flex items-center gap-0.5">
                  {onEditUser && (
                    <button
                      type="button"
                      onClick={() => onEditUser(user.id)}
                      disabled={deletingUserId === user.id || bulkDeleting}
                      className="text-slate-600 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
                      title="Edit user">
                      <FaPen className="h-4 w-4" />
                    </button>
                  )}
                  {onDeleteUser && (
                    <button
                      type="button"
                      onClick={() => onDeleteUser(user)}
                      disabled={deletingUserId === user.id || bulkDeleting}
                      className="text-gray-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
                      title="Delete user (Admin or Developer only)">
                      {deletingUserId === user.id ? (
                        <span className="inline-block h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <FaTrash className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  {onOpenLoginDevices && (
                    <button
                      type="button"
                      onClick={() => onOpenLoginDevices(user)}
                      disabled={deletingUserId === user.id || bulkDeleting}
                      className="text-sky-600 hover:text-sky-700 p-1.5 rounded-lg hover:bg-sky-50 transition-colors disabled:opacity-40"
                      title="Login / logout history">
                      <MdDevices className="h-4 w-4" />
                    </button>
                  )}
                  {onLogoutAllDevices && (
                    <button
                      type="button"
                      onClick={() => onLogoutAllDevices(user)}
                      disabled={
                        deletingUserId === user.id ||
                        bulkDeleting ||
                        logoutBusyUserId === user.id
                      }
                      className="text-rose-600 hover:text-rose-700 p-1.5 rounded-lg hover:bg-rose-50 transition-colors disabled:opacity-40"
                      title="Logout all devices">
                      {logoutBusyUserId === user.id ? (
                        <span className="inline-block h-4 w-4 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <MdLogout className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onViewProfile(user.id)}
                    disabled={deletingUserId === user.id || bulkDeleting}
                    className="text-red-500 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
                    title="View profile">
                    <FaEye className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
